/**
 * One derivation per ticket fact.
 *
 * Phase 9's audit: a fact should have one *renderer*, not merely one location.
 * The header band's RESOLUTION DUE cell and the sidebar's SERVICE LEVEL bars are
 * a deliberate summary/detail pair — which only works if both read the same
 * numbers. Two percentage calculations that can disagree is the bug this exists
 * to prevent, so both call `deriveSlaState`.
 */

import { formatDateTime } from '@shared/format/datetime';

/**
 * `paused`: the clock is stopped because the ticket sits in one of its
 * pipeline's SLA pause statuses (by default `pending` when "Pause on Pending"
 * is on). Nothing counts down; the deadline moves out when it resumes.
 */
export type SlaState = 'met' | 'ok' | 'due' | 'breached' | 'paused';

export interface SlaPolicyState {
  /** Short mono figure for the right-hand caption, e.g. "2h 43m" / "BREACHED". */
  label: string;
  /** Elapsed against target, 0-100, clamped. */
  pct: number;
  state: SlaState;
  /** Longer form for the band cell, e.g. "3h 12m". */
  remainingLabel: string;
  /** Absolute target time, pre-formatted. */
  at: string;
}

export interface SlaDerivation {
  firstResponse?: SlaPolicyState;
  resolution?: SlaPolicyState;
}

/** Compact duration, mono-friendly: "41m", "6h 20m", "2d 04h". */
export function compactDuration(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
  return `${Math.floor(hours / 24)}d ${String(hours % 24).padStart(2, '0')}h`;
}

export function absoluteTime(date?: string | null): string {
  // #18929 — render in the user's preferred timezone (Settings → Account),
  // not the browser's, so Opened / First response / Resolution-due honour it.
  return formatDateTime(date, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * One policy's state. `metAt` short-circuits: a policy that has already been
 * satisfied is MET regardless of the clock.
 */
function derivePolicy(
  startedAt?: string | null,
  dueAt?: string | null,
  metAt?: string | null,
  pausedAt?: string | null,
): SlaPolicyState | undefined {
  if (!dueAt) return undefined;
  const due = new Date(dueAt).getTime();
  if (Number.isNaN(due)) return undefined;

  const at = absoluteTime(dueAt);

  if (metAt) {
    const met = new Date(metAt).getTime();
    const start = startedAt ? new Date(startedAt).getTime() : NaN;
    const took = !Number.isNaN(met) && !Number.isNaN(start) ? compactDuration(met - start) : '';
    return { label: took, pct: 100, state: 'met', remainingLabel: took, at };
  }

  // Paused: freeze the clock at the moment the pause began. The persisted
  // deadline is only pushed out on resume, so counting down to it now would
  // show a breach that is not happening.
  const paused = pausedAt ? new Date(pausedAt).getTime() : NaN;
  const now = Number.isNaN(paused) ? Date.now() : Math.min(Date.now(), paused);
  if (pausedAt !== undefined && pausedAt !== null) {
    const start = startedAt ? new Date(startedAt).getTime() : NaN;
    const window = Number.isNaN(start) ? NaN : due - start;
    const pct =
      !Number.isNaN(window) && window > 0
        ? Math.min(100, Math.max(0, ((now - start) / window) * 100))
        : 0;
    const left = due - now;
    return {
      label: 'PAUSED',
      pct,
      state: 'paused',
      remainingLabel: left > 0 ? `PAUSED · ${compactDuration(left)} left` : 'PAUSED',
      at,
    };
  }

  const remaining = due - now;
  if (remaining <= 0) {
    // A mono state word, not prose — the band and the bars both render it raw.
    return { label: 'BREACHED', pct: 100, state: 'breached', remainingLabel: 'BREACHED', at };
  }

  const start = startedAt ? new Date(startedAt).getTime() : NaN;
  const window = Number.isNaN(start) ? NaN : due - start;
  const pct =
    !Number.isNaN(window) && window > 0
      ? Math.min(100, Math.max(0, ((now - start) / window) * 100))
      : 0;

  // Under a quarter of the window left is the warning threshold.
  const state: SlaState = !Number.isNaN(window) && window > 0 && remaining / window < 0.25 ? 'due' : 'ok';
  const label = compactDuration(remaining);
  return { label, pct, state, remainingLabel: label, at };
}

/**
 * The SLA pause the ticket-service records on the ticket
 * (`metadata.slaPause`). Returns the ISO time the current pause began, or
 * null when the clock is running.
 */
export function slaPausedAt(ticket: any): string | null {
  const pause = ticket?.metadata?.slaPause;
  if (!pause || pause.isPaused !== true) return null;
  return typeof pause.pausedAt === 'string' ? pause.pausedAt : new Date().toISOString();
}

/**
 * Both SLA policies for a ticket. Loosely typed — the SLA payload's shape has
 * varied and this reads only the fields that have been stable.
 */
export function deriveSlaState(ticket: any): SlaDerivation {
  if (!ticket) return {};
  // A resolved or closed ticket has no live countdown.
  const settled = ticket.status === 'resolved' || ticket.status === 'closed';
  const pausedAt = settled ? null : slaPausedAt(ticket);

  const resolution = derivePolicy(
    ticket.createdAt,
    ticket.resolutionDueAt,
    ticket.resolvedAt ?? (settled ? ticket.updatedAt : null),
    pausedAt,
  );
  const firstResponse = derivePolicy(
    ticket.createdAt,
    ticket.firstResponseDueAt,
    ticket.firstRespondedAt ?? ticket.firstResponseAt,
    pausedAt,
  );

  return { firstResponse, resolution };
}

/** Elapsed since the ticket was opened, plus the absolute timestamp. */
export function deriveOpened(createdAt?: string | null) {
  if (!createdAt) return undefined;
  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) return undefined;
  return { elapsed: compactDuration(Date.now() - t), at: absoluteTime(createdAt) };
}

/**
 * The ticket a merged ticket was merged into, or undefined when the ticket is
 * not merged (ticket-service sets `status: 'merged'` + `metadata.mergedInto`).
 */
export function mergedIntoTicketId(ticket: any): string | undefined {
  if (ticket?.status !== 'merged') return undefined;
  const target = ticket?.metadata?.mergedInto;
  return typeof target === 'string' && target ? target : undefined;
}
